import { useEffect, useState } from 'react';
import { formatNumber } from '@/lib/format';

export function AnimatedNumber({
  value,
  suffix = '',
  className = '',
}: {
  value: number;
  suffix?: string;
  className?: string;
}) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const duration = 1200;
    const steps = 40;
    const increment = value / steps;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setDisplay(Math.min(Math.round(increment * step), value));
      if (step >= steps) clearInterval(interval);
    }, duration / steps);
    return () => clearInterval(interval);
  }, [value]);

  return (
    <span className={className}>
      {formatNumber(display)}
      {suffix}
    </span>
  );
}

export function StatCard({
  icon: Icon,
  iconBg,
  iconColor,
  label,
  value,
  suffix,
  children,
  onClick,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number;
  suffix?: string;
  children?: React.ReactNode;
  onClick?: () => void;
}) {
  const baseClass = 'rounded-2xl bg-white p-5 shadow-sm';
  const interactiveClass = onClick
    ? ' w-full text-left cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500'
    : '';

  const content = (
    <>
      <div className="flex items-start justify-between">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg}`}
        >
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
      </div>
      <div className="mt-3">
        <div className="text-3xl font-extrabold text-slate-900">
          <AnimatedNumber value={value} suffix={suffix} />
        </div>
        <p className="text-sm text-slate-500 mt-1">{label}</p>
      </div>
      {children}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={baseClass + interactiveClass}>
        {content}
      </button>
    );
  }

  return <div className={baseClass}>{content}</div>;
}
